import React, { useState } from 'react';
import { Info, Plus, Check, X, AlertCircle, Sparkles, GripVertical, Edit, Trash2, Camera, Lightbulb, Home, MapPin, Maximize, Layers, DoorOpen, Bath } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
// Types
interface DesiderataItem {
  id: string;
  text: string;
  active: boolean;
}
interface PhotoItem {
  id: string;
  url: string;
  room: string;
}
export function SchedaProprietaPage() {
  // State: General Info
  const [generalInfo, setGeneralInfo] = useState({
    address: 'Via dei Giardini 12',
    city: 'Milano',
    floor: '3',
    sqm: '120',
    rooms: '4',
    bathrooms: '2'
  });
  // State: Materials
  const [materials, setMaterials] = useState<string[]>(['Parquet antico', 'Marmo Carrara', 'Tessuti delicati']);
  const [newMaterial, setNewMaterial] = useState('');
  const [materialNote, setMaterialNote] = useState('Il parquet in salotto è molto delicato, usare solo prodotti specifici e panno ben strizzato.');
  // State: Products
  const [favProducts, setFavProducts] = useState<string[]>(['Detergente Bio', 'Anticalcare X', 'Sgrassatore Limone']);
  const [avoidProducts, setAvoidProducts] = useState<string[]>(['Candeggina', 'Ammoniaca']);
  const [newFavProduct, setNewFavProduct] = useState('');
  const [newAvoidProduct, setNewAvoidProduct] = useState('');
  // State: Access
  const [accessInfo, setAccessInfo] = useState({
    type: 'codice',
    code: '1234 #',
    notes: 'Il tastierino si trova sulla sinistra del portone.'
  });
  // Tab State
  const [activeTab, setActiveTab] = useState('informazioni');
  // State: Storico Filter
  const [storicoFilter, setStoricoFilter] = useState<'tutti' | 'pulizie' | 'straordinari'>('tutti');
  // Mock data: Storico interventions
  const storicoData = [{
    id: '1',
    type: 'pulizia',
    icon: '🧹',
    title: 'Pulizia ordinaria',
    date: '15 Gen 2024',
    duration: '3h 45min',
    status: 'Completato'
  }, {
    id: '2',
    type: 'straordinario',
    icon: '⚡',
    title: 'Lampadina bagno',
    date: '15 Gen 2024',
    duration: '30min',
    status: 'Completato'
  }, {
    id: '3',
    type: 'pulizia',
    icon: '🧹',
    title: 'Deep cleaning',
    date: '10 Gen 2024',
    duration: '5h',
    status: 'Completato'
  }, {
    id: '4',
    type: 'straordinario',
    icon: '⚡',
    title: 'Riparazione rubinetto',
    date: '8 Gen 2024',
    duration: '1h 15min',
    status: 'Completato'
  }, {
    id: '5',
    type: 'pulizia',
    icon: '🧹',
    title: 'Pulizia ordinaria',
    date: '5 Gen 2024',
    duration: '3h 30min',
    status: 'Completato'
  }];
  // Filter storico data based on selected filter
  const filteredStorico = storicoData.filter(item => {
    if (storicoFilter === 'tutti') return true;
    if (storicoFilter === 'pulizie') return item.type === 'pulizia';
    if (storicoFilter === 'straordinari') return item.type === 'straordinario';
    return true;
  });
  // State: Desiderata
  const [desiderata, setDesiderata] = useState<DesiderataItem[]>([{
    id: '1',
    text: 'Lenzuola sempre stirate',
    active: true
  }, {
    id: '2',
    text: 'Cuscini divano sistemati in modo specifico',
    active: true
  }, {
    id: '3',
    text: 'Asciugamani piegati a cigno',
    active: false
  }, {
    id: '4',
    text: 'Lasciare luci ingresso accese',
    active: true
  }]);
  const [newDesiderata, setNewDesiderata] = useState('');
  // State: Photos
  const [photos, setPhotos] = useState<PhotoItem[]>([{
    id: '1',
    url: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=400',
    room: 'Soggiorno'
  }, {
    id: '2',
    url: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&q=80&w=400',
    room: 'Cucina'
  }, {
    id: '3',
    url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=400',
    room: 'Camera Master'
  }]);
  // Handlers
  const handleAddMaterial = () => {
    if (newMaterial.trim()) {
      setMaterials([...materials, newMaterial.trim()]);
      setNewMaterial('');
    }
  };
  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  const handleAddFavProduct = () => {
    if (newFavProduct.trim()) {
      setFavProducts([...favProducts, newFavProduct.trim()]);
      setNewFavProduct('');
    }
  };
  const handleAddAvoidProduct = () => {
    if (newAvoidProduct.trim()) {
      setAvoidProducts([...avoidProducts, newAvoidProduct.trim()]);
      setNewAvoidProduct('');
    }
  };
  const toggleDesiderata = (id: string) => {
    setDesiderata(desiderata.map(item => item.id === id ? {
      ...item,
      active: !item.active
    } : item));
  };
  const deleteDesiderata = (id: string) => {
    setDesiderata(desiderata.filter(item => item.id !== id));
  };
  const addDesiderata = () => {
    if (newDesiderata.trim()) {
      setDesiderata([...desiderata, {
        id: Date.now().toString(),
        text: newDesiderata.trim(),
        active: true
      }]);
      setNewDesiderata('');
    }
  };
  const addAISuggestion = () => {
    setDesiderata([...desiderata, {
      id: Date.now().toString(),
      text: 'Attenzione al parquet zona ingresso',
      active: true
    }]);
  };
  return <MobileLayout data-id="element-1732">
      {/* Page Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8" data-id="element-1733">
        <div data-id="element-1734">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1735">
            Scheda Proprietà
          </h1>
          <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1736">
            Villa Sunset - Milano
          </p>
        </div>
        <button className="w-full sm:w-auto px-4 py-2.5 bg-teal-deep text-white rounded-full hover:bg-[#50BFD9] transition-colors text-sm min-h-[44px]" data-id="element-1737">
          Salva Modifiche
        </button>
      </div>

      {/* Tab Navigation - Underline style like Pulizie page */}
      <div className="flex items-center gap-6 mb-6 overflow-x-auto pb-1 -mx-4 px-4 lg:mx-0 lg:px-0" data-id="element-1738">
        {[{
        id: 'informazioni',
        label: 'Informazioni'
      }, {
        id: 'accesso',
        label: 'Accesso'
      }, {
        id: 'preferenze',
        label: 'Preferenze'
      }, {
        id: 'attenzione',
        label: 'Punti di Attenzione'
      }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${activeTab === tab.id ? 'border-teal-deep text-teal-deep' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1739">
            {tab.label}
          </button>)}
      </div>

      {/* Info Generali Card - Tab: Informazioni */}
      {activeTab === 'informazioni' && <>
          <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6 mb-4 lg:mb-6" data-id="element-1740">
            <div className="mb-4 lg:mb-6" data-id="element-1741">
              <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-1742">
                <Home size={18} data-id="element-1743" />
                Info Generali
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-1744">
              <div data-id="element-1745">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1746">
                  Indirizzo
                </label>
                <div className="relative" data-id="element-1747">
                  <MapPin className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-1748" />
                  <input type="text" value={generalInfo.address} onChange={e => setGeneralInfo({
                ...generalInfo,
                address: e.target.value
              })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-1749" />
                </div>
              </div>
              <div data-id="element-1750">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1751">
                  Città
                </label>
                <input type="text" value={generalInfo.city} onChange={e => setGeneralInfo({
              ...generalInfo,
              city: e.target.value
            })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-1752" />
              </div>
              <div data-id="element-1753">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1754">
                  Piano
                </label>
                <div className="relative" data-id="element-1755">
                  <Layers className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-1756" />
                  <input type="text" value={generalInfo.floor} onChange={e => setGeneralInfo({
                ...generalInfo,
                floor: e.target.value
              })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-1757" />
                </div>
              </div>
              <div data-id="element-1758">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1759">
                  Mq
                </label>
                <div className="relative" data-id="element-1760">
                  <Maximize className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-1761" />
                  <input type="text" value={generalInfo.sqm} onChange={e => setGeneralInfo({
                ...generalInfo,
                sqm: e.target.value
              })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-1762" />
                </div>
              </div>
              <div data-id="element-1763">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1764">
                  Numero Stanze
                </label>
                <div className="relative" data-id="element-1765">
                  <DoorOpen className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-1766" />
                  <input type="text" value={generalInfo.rooms} onChange={e => setGeneralInfo({
                ...generalInfo,
                rooms: e.target.value
              })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-1767" />
                </div>
              </div>
              <div data-id="element-1768">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1769">
                  Numero Bagni
                </label>
                <div className="relative" data-id="element-1770">
                  <Bath className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-1771" />
                  <input type="text" value={generalInfo.bathrooms} onChange={e => setGeneralInfo({
                ...generalInfo,
                bathrooms: e.target.value
              })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-1772" />
                </div>
              </div>
            </div>
          </div>

          {/* Foto Riferimento Card */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-1773">
            <div className="mb-4 lg:mb-6" data-id="element-1774">
              <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-1775">
                <Camera size={18} data-id="element-1776" />
                Foto Riferimento
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1777">
                Queste foto verranno usate dall'AI per confrontare il prima/dopo
              </p>
            </div>

            <div className="space-y-4 lg:space-y-6" data-id="element-1778">
              {Array.from(new Set(photos.map(p => p.room))).map(roomName => {
            const roomPhotos = photos.filter(p => p.room === roomName);
            return <div key={roomName} className="space-y-3" data-id="element-1779">
                      <div className="flex items-center justify-between" data-id="element-1780">
                        <h3 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-1781">
                          <DoorOpen size={16} className="text-teal-deep" data-id="element-1782" />
                          {roomName}
                        </h3>
                        <span className="text-xs text-gray-500" data-id="element-1783">
                          {roomPhotos.length} foto
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3 p-3 lg:p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1784">
                        {roomPhotos.map(photo => <div key={photo.id} className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100" data-id="element-1785">
                            <img src={photo.url} alt={photo.room} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-1786" />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-start justify-end p-2" data-id="element-1787">
                              <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex gap-1" data-id="element-1788">
                                <button className="p-1.5 bg-white text-gray-700 rounded-full hover:text-navy shadow-sm" data-id="element-1789">
                                  <Edit size={12} data-id="element-1790" />
                                </button>
                                <button onClick={() => setPhotos(photos.filter(p => p.id !== photo.id))} className="p-1.5 bg-white text-red-500 rounded-full hover:text-red-700 shadow-sm" data-id="element-1791">
                                  <Trash2 size={12} data-id="element-1792" />
                                </button>
                              </div>
                            </div>
                          </div>)}

                        <button className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-teal-deep hover:bg-white transition-all flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-teal-deep min-h-[44px]" data-id="element-1793">
                          <Plus size={20} data-id="element-1794" />
                          <span className="text-xs font-medium" data-id="element-1795">Aggiungi</span>
                        </button>
                      </div>
                    </div>;
          })}

              <button className="w-full p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-mint-light transition-all flex items-center justify-center gap-2 text-gray-500 hover:text-teal-deep min-h-[44px]" data-id="element-1796">
                <Plus size={18} data-id="element-1797" />
                <span className="text-sm font-medium" data-id="element-1798">
                  Aggiungi nuova stanza
                </span>
              </button>
            </div>
          </div>
        </>}

      {/* Punti di Attenzione Tab */}
      {activeTab === 'attenzione' && <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-1799">
          <div className="mb-4 lg:mb-6" data-id="element-1800">
            <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-1801">
              <Info size={18} data-id="element-1802" />
              Materiali & Attenzioni
            </h2>
            <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1803">
              Indica i materiali presenti che richiedono attenzione particolare
            </p>
          </div>

          <div className="space-y-4" data-id="element-1804">
            <div className="flex flex-wrap gap-2" data-id="element-1805">
              {materials.map((material, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-1806">
                  {material}
                  <button onClick={() => removeMaterial(index)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-1807">
                    <X size={14} data-id="element-1808" />
                  </button>
                </span>)}
            </div>

            <div className="flex gap-2" data-id="element-1809">
              <input type="text" value={newMaterial} onChange={e => setNewMaterial(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddMaterial()} placeholder="Aggiungi materiale..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-1810" />
              <button onClick={handleAddMaterial} disabled={!newMaterial.trim()} className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1811">
                <Plus size={18} data-id="element-1812" />
              </button>
            </div>

            <div data-id="element-1813">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1814">
                Note aggiuntive sui materiali
              </label>
              <textarea value={materialNote} onChange={e => setMaterialNote(e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all resize-none" placeholder="Inserisci dettagli specifici su come trattare le superfici..." data-id="element-1815" />
            </div>
          </div>
        </div>}

      {/* Preferenze Pulizia Tab */}
      {activeTab === 'preferenze' && <>
          <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6 mb-4 lg:mb-6" data-id="element-1816">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-1817">
              {/* Preferred Products */}
              <div data-id="element-1818">
                <div className="mb-4" data-id="element-1819">
                  <h3 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-1820">
                    <div className="p-1 bg-green-100 rounded-full text-green-600" data-id="element-1821">
                      <Check size={14} data-id="element-1822" />
                    </div>
                    Prodotti Preferiti
                  </h3>
                </div>

                <div data-id="element-1823">
                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-1824">
                    {favProducts.map((product, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-1825">
                        {product}
                        <button onClick={() => setFavProducts(favProducts.filter((_, i) => i !== index))} className="ml-2 text-green-500 hover:text-green-700" data-id="element-1826">
                          <X size={14} data-id="element-1827" />
                        </button>
                      </span>)}
                  </div>
                  <div className="flex gap-2" data-id="element-1828">
                    <input type="text" value={newFavProduct} onChange={e => setNewFavProduct(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddFavProduct()} placeholder="Nuovo prodotto..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none min-h-[44px]" data-id="element-1829" />
                    <button onClick={handleAddFavProduct} disabled={!newFavProduct.trim()} className="px-3 py-2.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1830">
                      <Plus size={18} data-id="element-1831" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Avoided Products */}
              <div data-id="element-1832">
                <div className="mb-4" data-id="element-1833">
                  <h3 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-1834">
                    <div className="p-1 bg-red-100 rounded-full text-red-600" data-id="element-1835">
                      <X size={14} data-id="element-1836" />
                    </div>
                    Prodotti da Evitare
                  </h3>
                </div>

                <div data-id="element-1837">
                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-1838">
                    {avoidProducts.map((product, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100" data-id="element-1839">
                        {product}
                        <button onClick={() => setAvoidProducts(avoidProducts.filter((_, i) => i !== index))} className="ml-2 text-red-500 hover:text-red-700" data-id="element-1840">
                          <X size={14} data-id="element-1841" />
                        </button>
                      </span>)}
                  </div>
                  <div className="flex gap-2" data-id="element-1842">
                    <input type="text" value={newAvoidProduct} onChange={e => setNewAvoidProduct(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddAvoidProduct()} placeholder="Prodotto da evitare..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none min-h-[44px]" data-id="element-1843" />
                    <button onClick={handleAddAvoidProduct} disabled={!newAvoidProduct.trim()} className="px-3 py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1844">
                      <Plus size={18} data-id="element-1845" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desiderata Card */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-1846">
            <div className="mb-4 lg:mb-6" data-id="element-1847">
              <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-1848">
                <Sparkles size={18} className="text-coral" data-id="element-1849" />
                Desiderata Pulizia
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1850">
                Questi elementi verranno aggiunti automaticamente alla checklist
                del fornitore
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-1851">
              {desiderata.map(item => <div key={item.id} className="group flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-all" data-id="element-1852">
                  <div className="cursor-grab text-gray-400 hover:text-gray-600 hidden sm:block" data-id="element-1853">
                    <GripVertical size={18} data-id="element-1854" />
                  </div>
                  <button onClick={() => toggleDesiderata(item.id)} className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-deep focus:ring-offset-2 flex-shrink-0 ${item.active ? 'bg-teal-deep' : 'bg-gray-300'}`} data-id="element-1855">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.active ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-1856" />
                  </button>
                  <span className={`flex-1 text-sm ${item.active ? 'text-gray-700' : 'text-gray-400 line-through'}`} data-id="element-1857">
                    {item.text}
                  </span>
                  <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-1858">
                    <button className="p-1.5 text-gray-400 hover:text-navy hover:bg-white rounded-md" data-id="element-1859">
                      <Edit size={14} data-id="element-1860" />
                    </button>
                    <button onClick={() => deleteDesiderata(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-md" data-id="element-1861">
                      <Trash2 size={14} data-id="element-1862" />
                    </button>
                  </div>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-1863">
              <input type="text" value={newDesiderata} onChange={e => setNewDesiderata(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDesiderata()} placeholder="Aggiungi una nuova richiesta specifica..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-1864" />
              <button onClick={addDesiderata} disabled={!newDesiderata.trim()} className="px-4 py-2.5 bg-teal-deep text-white rounded-full hover:bg-[#50BFD9] disabled:opacity-50 font-medium text-sm min-h-[44px] whitespace-nowrap" data-id="element-1865">
                Aggiungi
              </button>
            </div>

            <div className="pt-6 border-t border-gray-100" data-id="element-1866">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3" data-id="element-1867">
                <div className="p-2 bg-white rounded-full text-yellow-500 shadow-sm flex-shrink-0" data-id="element-1868">
                  <Lightbulb size={18} data-id="element-1869" />
                </div>
                <div className="flex-1" data-id="element-1870">
                  <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-1871">
                    Suggerimento AI
                  </p>
                  <p className="text-xs text-blue-700 mb-3" data-id="element-1872">
                    In base alle segnalazioni precedenti, considera di
                    aggiungere "Attenzione al parquet zona ingresso"
                  </p>
                  <button onClick={addAISuggestion} className="text-xs font-medium text-blue-600 hover:text-blue-800 underline" data-id="element-1873">
                    Aggiungi ai desiderata
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Accesso Tab */}
      {activeTab === 'accesso' && <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-1874">
          <div className="mb-4 lg:mb-6" data-id="element-1875">
            <h2 className="text-sm font-medium text-navy" data-id="element-1876">
              Istruzioni Accesso
            </h2>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 lg:p-6 rounded-r-lg mb-6" data-id="element-1877">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-1878">
              <div data-id="element-1879">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1880">
                  Tipo Accesso
                </label>
                <select value={accessInfo.type} onChange={e => setAccessInfo({
              ...accessInfo,
              type: e.target.value
            })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-1881">
                  <option value="chiavi" data-id="element-1882">Chiavi fisiche</option>
                  <option value="codice" data-id="element-1883">Codice tastierino</option>
                  <option value="portineria" data-id="element-1884">Portineria</option>
                  <option value="smart" data-id="element-1885">Smart Lock</option>
                  <option value="vickey" data-id="element-1886">Vickey</option>
                </select>
              </div>
              <div data-id="element-1887">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1888">
                  Codice / Info Chiavi
                </label>
                <input type="text" value={accessInfo.code} onChange={e => setAccessInfo({
              ...accessInfo,
              code: e.target.value
            })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none min-h-[44px]" data-id="element-1889" />
              </div>
              <div className="sm:col-span-2" data-id="element-1890">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-1891">
                  Note Accesso
                </label>
                <textarea value={accessInfo.notes} onChange={e => setAccessInfo({
              ...accessInfo,
              notes: e.target.value
            })} rows={2} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none resize-none" data-id="element-1892" />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100" data-id="element-1893">
            <div className="flex items-start gap-2 text-xs text-blue-700 font-medium" data-id="element-1894">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" data-id="element-1895" />
              <span data-id="element-1896">
                Queste informazioni saranno visibili al fornitore solo dopo
                conferma intervento
              </span>
            </div>
          </div>
        </div>}

      {/* Storico Tab */}
      {activeTab === 'storico' && <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-1897">
          <div className="mb-4 lg:mb-6" data-id="element-1898">
            <h2 className="text-sm font-medium text-navy" data-id="element-1899">
              Storico Interventi
            </h2>
            <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1900">
              Cronologia di tutti gli interventi effettuati
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 mb-4" data-id="element-1901">
            <button onClick={() => setStoricoFilter('tutti')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'tutti' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1902">
              Tutti
            </button>
            <button onClick={() => setStoricoFilter('pulizie')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'pulizie' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1903">
              Pulizie
            </button>
            <button onClick={() => setStoricoFilter('straordinari')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'straordinari' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1904">
              Straordinari
            </button>
          </div>

          {/* History List */}
          <div className="space-y-3" data-id="element-1905">
            {filteredStorico.map(item => <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-1906">
                <div className="flex items-center gap-3" data-id="element-1907">
                  <span className="text-lg" data-id="element-1908">{item.icon}</span>
                  <div data-id="element-1909">
                    <div className="text-sm font-medium text-navy" data-id="element-1910">
                      {item.title}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-1911">{item.date}</div>
                  </div>
                </div>
                <div className="text-right" data-id="element-1912">
                  <div className="text-sm text-gray-600" data-id="element-1913">{item.duration}</div>
                  <div className="text-xs text-green-600" data-id="element-1914">✓ {item.status}</div>
                </div>
              </div>)}

            {filteredStorico.length === 0 && <div className="text-center py-8 text-gray-400 text-sm" data-id="element-1915">
                Nessun intervento trovato
              </div>}
          </div>
        </div>}
    </MobileLayout>;
}